/**
 * The room's camera: what part of the room is in view, and at what zoom.
 *
 * Expressed as a centre point and a zoom, in room units, and turned into an SVG viewBox. At zoom 1
 * the view is one area wide — the size the Bug Hunt is drawn at — so figures read at their natural
 * size; zooming out shows more areas at once, zooming in gets close to one desk.
 *
 * Moves between areas are tweened in a few hundred milliseconds so the viewer sees where they went;
 * with reduced motion they jump.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { AREA_WIDTH, ROOM_WIDTH } from './areas'

export interface Camera {
  cx: number
  cy: number
  zoom: number
}

export const MIN_ZOOM = 0.3
export const MAX_ZOOM = 2.5

const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))

export function useCamera(height: number, reducedMotion: boolean) {
  const [camera, setCamera] = useState<Camera>({ cx: AREA_WIDTH / 2, cy: height / 2, zoom: 1 })
  const frame = useRef<number | null>(null)

  // Keeps the view vertically centred on the room when its height changes with the window.
  useEffect(() => {
    setCamera((current) => ({ ...current, cy: height / 2 }))
  }, [height])

  const stop = () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current)
    frame.current = null
  }

  useEffect(() => stop, [])

  /** Moves to a camera, tweened unless motion is reduced. */
  const moveTo = useCallback(
    (target: Camera) => {
      stop()
      if (reducedMotion) {
        setCamera(target)
        return
      }

      let start: Camera | null = null
      const began = performance.now()
      const duration = 450

      const step = (now: number) => {
        setCamera((current) => {
          start ??= current
          const t = Math.min(1, (now - began) / duration)
          const ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
          return {
            cx: start.cx + (target.cx - start.cx) * ease,
            cy: start.cy + (target.cy - start.cy) * ease,
            zoom: start.zoom + (target.zoom - start.zoom) * ease,
          }
        })
        if (now - began < duration) frame.current = requestAnimationFrame(step)
        else frame.current = null
      }

      frame.current = requestAnimationFrame(step)
    },
    [reducedMotion],
  )

  /** Zooms by a factor, keeping `around` (room units) where it is on screen. */
  const zoomBy = useCallback((factor: number, around?: { x: number; y: number }) => {
    stop()
    setCamera((current) => {
      const zoom = clampZoom(current.zoom * factor)
      if (!around) return { ...current, zoom }
      const k = current.zoom / zoom
      return { zoom, cx: around.x + (current.cx - around.x) * k, cy: around.y + (current.cy - around.y) * k }
    })
  }, [])

  const panBy = useCallback((dx: number, dy: number) => {
    stop()
    setCamera((current) => ({
      ...current,
      cx: Math.min(ROOM_WIDTH, Math.max(0, current.cx + dx)),
      cy: Math.min(height, Math.max(0, current.cy + dy)),
    }))
  }, [height])

  const focusArea = useCallback(
    (index: number) => moveTo({ cx: index * AREA_WIDTH + AREA_WIDTH / 2, cy: height / 2, zoom: 1 }),
    [moveTo, height],
  )

  const overview = useCallback(
    () => moveTo({ cx: ROOM_WIDTH / 2, cy: height / 2, zoom: AREA_WIDTH / ROOM_WIDTH }),
    [moveTo, height],
  )

  const focusPoint = useCallback(
    (x: number, y: number) => moveTo({ cx: x, cy: Math.min(height, Math.max(0, y)), zoom: Math.max(1.2, camera.zoom) }),
    [moveTo, height, camera.zoom],
  )

  /** The viewBox for a view one area wide at zoom 1, whatever the room's height. */
  const viewBox = (() => {
    const w = AREA_WIDTH / camera.zoom
    const h = height / camera.zoom
    return `${camera.cx - w / 2} ${camera.cy - h / 2} ${w} ${h}`
  })()

  return { camera, viewBox, zoomBy, panBy, focusArea, overview, focusPoint }
}
